import { useDisciplineColorMap } from '@/components/disciplines/useDisciplineColorMap';
import { VerticalStack } from '@/components/utils/linearLayouts';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { Discipline } from '@/types/graphql';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Box, Skeleton } from '@mui/material';
import moment from 'moment';
import { useMemo } from 'react';

import {
    CALENDAR_HEIGHT,
    MonthEventsCalendar,
} from '../../VisitsScheduling/calendar/MonthEventsCalendar';
import { CALENDAR_VISIT_FRAGMENT } from '../../VisitsScheduling/calendar/calendarVisit.fragment';
import {
    DisciplineColorContext,
    DisciplineLegend,
    StatusLegend,
} from '../../VisitsScheduling/calendar/components';
import {
    CalendarBar,
    CalendarDayMarker,
    CalendarTile,
} from '../../VisitsScheduling/calendar/types';
import { EpisodeTimeline } from './EpisodeTimeline';
import { PatientEpisodeCalendarVisitsQuery } from './PatientEpisodeCalendar.gql.types';
import { allEvents, createHUVEvent, episodeVisitToTiles } from './episodeEvents';
import { Episode } from './types';
import { getEpisodeCalendarRange, getEpisodeEndDate, getMonthsInRange } from './utils';

export const PATIENT_EPISODE_CALENDAR_VISITS_QUERY = gql`
    query PatientEpisodeCalendarVisits($patientId: UUID!, $startDate: Date!, $endDate: Date!) {
        patient(id: $patientId) {
            id
            hospice {
                hopeStartDate
                huv1Window {
                    startDate
                    endDate
                }
                huv2Window {
                    startDate
                    endDate
                }
            }
        }
        visits(
            filters: {
                patient: { id: { exact: $patientId } }
                effectiveDate: { gte: $startDate, lte: $endDate }
            }
        ) {
            items {
                ...CalendarVisit
            }
            totalCount
        }
    }
    ${CALENDAR_VISIT_FRAGMENT}
`;

/**
 * RBC library does not support showing large windows of time in a single calendar.
 * So we need to render multiple calendars.
 */
export const PatientEpisodeCalendar = ({
    episode,
    patientId,
    allEpisodes,
    scrollToEpisode,
    activeTabEpisodeId,
    onEpisodeClick,
}: {
    episode: Episode;
    patientId: string;
    allEpisodes?: Episode[];
    scrollToEpisode?: Episode;
    activeTabEpisodeId?: string | null;
    onEpisodeClick?: (episodeId: string) => void;
}) => {
    // When allEpisodes is provided, use the merged episode's date range for months.
    // Otherwise fall back to the single episode.
    const dateRanges = useMemo(() => getMonthsInRange(episode), [episode]);

    const calendarRange = useMemo(() => getEpisodeCalendarRange(episode), [episode]);
    const queryStartDate = useMemo(
        () => (calendarRange ? GraphInputHelpers.date(moment(calendarRange.start)) : null),
        [calendarRange],
    );
    const queryEndDate = useMemo(
        () => (calendarRange ? GraphInputHelpers.date(moment(calendarRange.end)) : null),
        [calendarRange],
    );

    const { data, loading } = useQuery<PatientEpisodeCalendarVisitsQuery>(
        PATIENT_EPISODE_CALENDAR_VISITS_QUERY,
        {
            variables: {
                patientId: patientId,
                startDate: queryStartDate,
                endDate: queryEndDate,
            },
        },
    );

    const disciplines = useMemo(() => {
        const seen = new Set<string>();
        const result: Pick<Discipline, 'id' | 'code'>[] = [];
        const allDisciplines = [...(data?.visits?.items.flatMap((v) => v.disciplines) ?? [])];
        allDisciplines.forEach((d) => {
            if (!seen.has(d.id)) {
                seen.add(d.id);
                result.push(d);
            }
        });
        return result.sort((a, b) => a.code.localeCompare(b.code));
    }, [data]);

    const disciplineColorMap = useDisciplineColorMap();

    const { events, dayMarkers } = useMemo(() => {
        const barEvents: CalendarBar[] = [];
        const tileEvents: CalendarTile[] = [];
        const markers: CalendarDayMarker[] = [];

        const huv1 = data?.patient?.hospice?.huv1Window;
        if (huv1) {
            barEvents.push(createHUVEvent(huv1.startDate, huv1.endDate, 'HUV1'));
        }
        const huv2 = data?.patient?.hospice?.huv2Window;
        if (huv2) {
            barEvents.push(createHUVEvent(huv2.startDate, huv2.endDate, 'HUV2'));
        }

        data?.visits?.items.forEach((visit) => {
            tileEvents.push(...episodeVisitToTiles(visit));
        });

        // When allEpisodes is provided, generate span primitives per episode and prefix each
        // id with the episode id to avoid collisions.
        if (allEpisodes && allEpisodes.length > 0) {
            allEpisodes.forEach((ep) => {
                if (!ep.startDate) {
                    return;
                }
                const { bars, markers: epMarkers } = allEvents({
                    ...ep,
                    day0: ep.startDate,
                    endDate: getEpisodeEndDate(ep),
                });
                barEvents.push(...bars.map((bar) => ({ ...bar, id: `${ep.id}-${bar.id}` })));
                markers.push(
                    ...epMarkers.map((marker) => ({ ...marker, id: `${ep.id}-${marker.id}` })),
                );
            });
        } else {
            const { bars, markers: epMarkers } = allEvents({
                ...episode,
                day0: episode.startDate ?? new Date(),
                endDate: getEpisodeEndDate(episode),
            });
            barEvents.push(...bars);
            markers.push(...epMarkers);
        }

        return { events: [...barEvents, ...tileEvents], dayMarkers: markers };
    }, [data, episode, allEpisodes]);

    if (loading || !data) {
        return <Skeleton variant='rectangular' height={CALENDAR_HEIGHT} />;
    }

    return (
        <DisciplineColorContext.Provider value={disciplineColorMap}>
            <VerticalStack spacing={3} mt={3}>
                <Box display={'flex'} flexWrap={'wrap'} gap={3}>
                    <StatusLegend />
                    {disciplines.length > 0 && <DisciplineLegend disciplines={disciplines} />}
                </Box>
                <Box display='flex' alignItems='flex-start'>
                    {allEpisodes && onEpisodeClick && (
                        <EpisodeTimeline
                            allEpisodes={allEpisodes}
                            containerHeight={CALENDAR_HEIGHT}
                            activeTabEpisodeId={activeTabEpisodeId}
                            onEpisodeClick={onEpisodeClick}
                        />
                    )}
                    <MonthEventsCalendar
                        events={events}
                        dayMarkers={dayMarkers}
                        months={dateRanges}
                        disciplineColorMap={disciplineColorMap}
                        scrollToMonth={scrollToEpisode?.startDate ?? null}
                    />
                </Box>
            </VerticalStack>
        </DisciplineColorContext.Provider>
    );
};
